var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { DateTime, IANAZone, Settings } from 'luxon';
import { ErrorLogType, WEEKLY_SCHEDULE_GRID_CONTAINER } from '..';
import { DimensionConstants } from '../dimensions.consts';
export var getErrorEventContext = function (_a) {
    var id = _a.id, startTime = _a.startTime, endTime = _a.endTime, type = _a.type;
    return {
        errorEventId: id,
        errorEventType: type,
        errorEventTimeRange: "".concat(startTime.toFormat('hh:mm a, yyyy/MM/dd'), " - ").concat(endTime.toFormat('hh:mm a, yyyy/MM/dd'))
    };
};
export var eventTimeSanityCheck = function (event, emitErrorLog) {
    var startTime = event.startTime, endTime = event.endTime;
    var errorMsg = '';
    if (!startTime.isValid) {
        errorMsg += "start time is not valid time, reason: ".concat(startTime.invalidExplanation, ";");
    }
    if (!endTime.isValid) {
        errorMsg += "end time is not valid time, reason: ".concat(startTime.invalidExplanation, ";");
    }
    if (endTime <= startTime) {
        errorMsg += "end time is before or equal to start time;";
    }
    if (errorMsg.length > 0) {
        emitErrorLog("".concat(ErrorLogType.InvalidEventTime, " - ").concat(errorMsg), __assign({ caller: 'eventTimeSanityCheck' }, getErrorEventContext(event)));
        return false;
    }
    return true;
};
export var getDisplayDetailsForEvents = function (events, eventTypeMapping, fallbackEventDetails, emitErrorLog) {
    var detailedEvents = [];
    for (var _i = 0, events_1 = events; _i < events_1.length; _i++) {
        var event_1 = events_1[_i];
        if (!eventTimeSanityCheck(event_1, emitErrorLog)) {
            continue;
        }
        var eventDetail = eventTypeMapping.get(event_1.type);
        if (!eventDetail) {
            eventDetail = fallbackEventDetails;
            emitErrorLog("".concat(ErrorLogType.InvalidEventType, " - type is not included in the eventTypeMapping"), __assign({ caller: 'getDisplayDetailsForEvents', eventTypeMappingKeys: Array.from(eventTypeMapping.keys()).join(',') }, getErrorEventContext(event_1)));
        }
        var type = event_1.type, eventWithoutType = __rest(event_1, ["type"]);
        detailedEvents.push(__assign(__assign({}, eventWithoutType), { displayDetails: eventDetail }));
    }
    detailedEvents.sort(function (a, b) { return a.startTime.toMillis() - b.startTime.toMillis(); });
    return detailedEvents;
};
export var getSplitEventId = function (originalEventId, splitEventStart) { return "".concat(originalEventId, "-splitEvent-").concat(formatTime(splitEventStart)); };
/**
 * This function is designed to divide events that span multiple days into individual daily events. It takes
 * an optional time range parameter. When the time range is not provided, the function splits the event into
 * daily events and returns all of them. However, if a time range is given, the function only returns daily
 * events that fall within that specified range
 */
export var splitEventToDailyEvents = function (rawEvent, timeRange) {
    var startTime = rawEvent.startTime, endTime = rawEvent.endTime;
    if (timeRange) {
        if (startTime >= timeRange.end || endTime <= timeRange.start) {
            return [];
        }
    }
    if (startTime.hasSame(endTime, 'day')) {
        return [rawEvent];
    }
    var dailyEvents = [];
    var splitEvent = function (event, eventStartTime, end) {
        var nextEnd = eventStartTime.endOf(end);
        dailyEvents.push(__assign(__assign({}, event), { id: getSplitEventId(event.id, eventStartTime), startTime: eventStartTime, endTime: nextEnd }));
        return nextEnd.plus({ milliseconds: 1 });
    };
    var eventStartPointer = DateTime.max(startTime, timeRange ? timeRange.start : startTime);
    var validEndTime = DateTime.min(endTime, timeRange ? timeRange.end : endTime);
    while (eventStartPointer < validEndTime) {
        if (eventStartPointer < validEndTime.endOf('day')) {
            eventStartPointer = splitEvent(rawEvent, eventStartPointer, 'day');
        }
        else if (eventStartPointer < validEndTime.endOf('month')) {
            eventStartPointer = splitEvent(rawEvent, eventStartPointer, 'month');
        }
        else {
            eventStartPointer = splitEvent(rawEvent, eventStartPointer, 'year');
        }
    }
    var lastEvent = dailyEvents[dailyEvents.length - 1];
    if (lastEvent.endTime > validEndTime) {
        lastEvent.endTime = validEndTime;
    }
    return dailyEvents;
};
export var initializeEventsByDateMap = function (eventTimeRange) {
    var eventsByDateMap = new Map();
    if (!eventTimeRange)
        return eventsByDateMap;
    var start = eventTimeRange.start, end = eventTimeRange.end;
    for (var date = start; date < end; date = date.plus({ day: 1 })) {
        eventsByDateMap.set(date.toISODate(), []);
    }
    return eventsByDateMap;
};
export var createEventsByDateMap = function (rawEvents, eventTimeRange) {
    var eventsByDateMap = initializeEventsByDateMap(eventTimeRange);
    rawEvents.forEach(function (rawEvent) {
        var dailyEvents = splitEventToDailyEvents(rawEvent, eventTimeRange);
        for (var _i = 0, dailyEvents_1 = dailyEvents; _i < dailyEvents_1.length; _i++) {
            var event_2 = dailyEvents_1[_i];
            var eventDate = event_2.startTime.toISODate();
            if (!eventsByDateMap.has(eventDate)) {
                eventsByDateMap.set(eventDate, []);
            }
            eventsByDateMap.get(eventDate).push(event_2);
        }
    });
    return eventsByDateMap;
};
export var getSplitsForEvent = function (eventsByDateMap, event) {
    var splitPoints = { splitNextEvent: undefined, splitPreviousEvent: undefined };
    if (event.endTime.toISO() === event.endTime.endOf('day').toISO()) {
        var nextDayEvents = eventsByDateMap.get(event.startTime.plus({ day: 1 }).toISODate());
        var nextEvent = nextDayEvents && nextDayEvents.length > 0
            ? nextDayEvents[0]
            : undefined;
        var isSplitCollection = nextEvent
            ? nextEvent.startTime.toISO() ===
                nextEvent.startTime.startOf('day').toISO() &&
                nextEvent.collectionId === event.collectionId
            : false;
        splitPoints.splitNextEvent = isSplitCollection ? nextEvent : undefined;
    }
    if (event.startTime.toISO() === event.startTime.startOf('day').toISO()) {
        var previousDayEvents = eventsByDateMap.get(event.startTime.minus({ day: 1 }).toISODate());
        var previousEvent = previousDayEvents && previousDayEvents.length > 0
            ? previousDayEvents[previousDayEvents.length - 1]
            : undefined;
        var isSplitCollection = previousEvent
            ? previousEvent.endTime.toISO() ===
                previousEvent.endTime.endOf('day').toISO() &&
                previousEvent.collectionId === event.collectionId
            : false;
        splitPoints.splitPreviousEvent = isSplitCollection
            ? previousEvent
            : undefined;
    }
    return splitPoints;
};
export var setLuxonLocale = function (locale) {
    Settings.defaultLocale = locale;
};
export var setLuxonTimeZone = function (timeZone, emitErrorLog) {
    if (IANAZone.isValidZone(timeZone)) {
        Settings.defaultZone = timeZone;
    }
    else if (emitErrorLog) {
        emitErrorLog(ErrorLogType.InvalidLuxonTimezone, {
            context: { timeZone: timeZone }
        });
    }
};
export var formatTime = function (dateTime) {
    return dateTime.toFormat('t').replace(' ', '').toLowerCase();
};
export var formatDate = function (dateTime) {
    return dateTime.toFormat('MMMM dd');
};
export var defaultEmitErrorLog = function (message, context) {
    console.error('WeeklyScheduleError: ' + message, context);
};
export var START_DATE_ATTRIBUTE_NAME = 'data-week-start-date';
var WeekRowError = /** @class */ (function (_super) {
    __extends(WeekRowError, _super);
    function WeekRowError(message) {
        var _this = _super.call(this) || this;
        _this.message = message;
        Object.setPrototypeOf(_this, WeekRowError.prototype);
        return _this;
    }
    return WeekRowError;
}(Error));
export { WeekRowError };
/*
    TODO: Once we begin utilizing Luxon DateTime in spyglass DatePicker, we should be able to
    deprecate this data string paring error.
    Reference: https://code.amazon.com/packages/AtoZSpyglass/blobs/9ab25bcf3e183029661e18117feb7094ffa77c78/--/packages/web-components/src/Datepicker/WeekRow/utils/utils.ts#L32
*/
var WeekRowStartDateParseError = /** @class */ (function (_super) {
    __extends(WeekRowStartDateParseError, _super);
    function WeekRowStartDateParseError(startDateString) {
        return _super.call(this, "Unable to convert the week start date string to a Luxon DateTime. The date string is ".concat(startDateString)) || this;
    }
    return WeekRowStartDateParseError;
}(WeekRowError));
export { WeekRowStartDateParseError };
export var getWeekRangeFromWeekRow = function (weekRowStartDate) {
    var dateString = weekRowStartDate.getAttribute(START_DATE_ATTRIBUTE_NAME);
    if (!dateString)
        return;
    try {
        var _a = dateString.split('-'), year = _a[0], month = _a[1], day = _a[2];
        var weekStart = DateTime.fromObject({
            year: Number(year),
            month: Number(month),
            day: Number(day)
        });
        return {
            start: weekStart.startOf('day'),
            end: weekStart.plus({ day: 6 }).endOf('day')
        };
    }
    catch (error) {
        throw new WeekRowStartDateParseError(dateString);
    }
};
/**
 * Calculates the width of the browser's scrollbar.
 * It works by creating a hidden dummy element with a scrollbar, measuring the difference between
 * its `offsetWidth`(total width) and 'clientWidth'(content width), and then removing the element
 * from the page. This approach can determine the scrollbar width in a browser-independent manner.
 * It ensures cross-browser compatibility without impacting the layout or appearance of the actual
 * page.
 *
 * Reference: https://stackoverflow.com/questions/8079187/how-to-calculate-the-width-of-the-scroll-bar
 */
export var getScrollbarWidth = function () {
    var dummyElement = document.createElement('div');
    dummyElement.id = 'dummyElement';
    dummyElement.style.width = '100px'; // Arbitrary value chosen to provide enough space for the scroll bar to appear
    dummyElement.style.height = '100px'; // Arbitrary value chosen to provide enough space for the scroll bar to appear
    dummyElement.style.overflow = 'scroll';
    dummyElement.style.top = '-9999px'; // The element is placed far above the visible area
    dummyElement.style.visibility = 'hidden';
    document.body.appendChild(dummyElement);
    var scrollbarWidth = dummyElement.offsetWidth - dummyElement.clientWidth;
    document.body.removeChild(dummyElement);
    return scrollbarWidth;
};
export var enablePageVerticalScroll = function (enable) {
    if (document.body.scrollHeight <= document.body.clientHeight)
        return false;
    if (enable) {
        document.body.style.overflowY = 'auto';
        document.body.style.paddingRight = '0px';
    }
    else {
        document.body.style.overflowY = 'hidden';
        document.body.style.paddingRight = "".concat(DimensionConstants.scrollbar.width, "px");
    }
    return true;
};
/**
 * Generate a distinct event block identifier which will subsequently be utilized for scrolling to the
 * corresponding event.
 */
export var getEventBlockId = function (event) {
    return "event".concat(event.startTime.toMillis()).concat(event.endTime.toMillis());
};
export var scrollScheduleGridTo = function (elementId, emitErrorLog) {
    try {
        var scheduleGrid = document.querySelector("#".concat(WEEKLY_SCHEDULE_GRID_CONTAINER));
        var element = document.querySelector("#".concat(elementId));
        if (!element)
            throw new Error('Cannot find the element to scroll to');
        if (!scheduleGrid)
            throw new Error('Cannot find scheduled grid');
        var gridRect = scheduleGrid.getBoundingClientRect();
        var elementRect = element.getBoundingClientRect();
        var offsetTop = elementRect.top - gridRect.top;
        scheduleGrid.scrollTop += offsetTop - DimensionConstants.edgeBuffer;
        return scheduleGrid.scrollTop;
    }
    catch (error) {
        emitErrorLog(ErrorLogType.FailedToScrollGridTo, {
            elementId: elementId,
            error: error instanceof Error ? error.toString() : error
        });
    }
};
// eslint-disable-next-line no-restricted-globals
export var getLocalJSDate = function (dateTime) {
    // eslint-disable-next-line no-restricted-globals
    return new Date(dateTime.get('year'), dateTime.get('month') - 1, dateTime.get('day'));
};
export var validateDayElements = function (dayElements, emitErrorLog) {
    if (dayElements.length === 7 &&
        findDateFromDayElement(dayElements[0]) &&
        findDateFromDayElement(dayElements[6])) {
        return true;
    }
    if (emitErrorLog) {
        var dayEleIds = [];
        for (var i = 0; i < dayElements.length; i++) {
            var dayContent = dayElements[i].querySelector('.day-content');
            dayEleIds.push(dayContent ? dayContent.getAttribute('id') : '');
        }
        emitErrorLog("Something wrong with day elements, day element ids are ".concat(dayEleIds.join(','), ";"));
    }
    return false;
};
export var findDateFromDayElement = function (dayElement
// eslint-disable-next-line no-restricted-globals
) {
    var day = dayElement === null || dayElement === void 0 ? void 0 : dayElement.querySelector('.day-content');
    var dateId = day === null || day === void 0 ? void 0 : day.getAttribute('id');
    /* TODO: This regEx is insufficient for distinguishing between valid and invalid dates. After luxon is available, consider using
     * luxon to validate the date instead. Plan to add luxon as dependency while migrate the weekly schedule into spyglass.
     * SIM: https://sim.amazon.com/issues/WSTT-579
     */
    if (!dateId || !/^date-\d{4}-\d{1,2}-\d{1,2}$/.test(dateId)) {
        return;
    }
    var _a = dateId.split('-'), _ = _a[0], yearStr = _a[1], monthStr = _a[2], dateStr = _a[3];
    // eslint-disable-next-line no-restricted-globals
    var date = new Date(Number(yearStr), Number(monthStr) - 1, Number(dateStr));
    return date;
};
export var getTimeRangeDurationText = function (timeRange) {
    if (!timeRange)
        return '';
    var start = timeRange.start.toFormat('MMMM dd, yyyy');
    var end = timeRange.end.toFormat('MMMM dd, yyyy');
    return "".concat(start, " - ").concat(end);
};
